import { PageHeader } from '@/shared/components/PageHeader';
import { PropsWithChildren, ReactNode, Suspense, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFulfillmentContext } from '../context/FulfillmentContext';
import { LoadingSpinner, MessageBox } from '@schuettflix/react-components';
import { FulfillmentSection } from './FulfillmentSection';
import { ClientInformationFormFulfillment } from './ClientInformationFormFulfillment';
import { ErrorBoundary } from '@/shared/components/ErrorBoundary.tsx';
import { ProductSelectionSection } from './ProductSelectionSection';
import { DocumentUploadSection } from './DocumentUploadSection';
import { Summary } from './Summary';
import { useWindowSize } from 'react-use';

export const FulfillmentBaseContent = ({
    headerTitle,
    headerAdditionalContent,
    footer,
}: PropsWithChildren<{
    headerTitle: string;
    headerAdditionalContent?: ReactNode;
    footer: ReactNode;
}>) => {
    const { t } = useTranslation();
    const { errors, isSubmitted } = useFulfillmentContext();

    // Error handling and scrolling logic
    useEffect(() => {
        if (!errors?.length || !isSubmitted) {
            return;
        }

        const timeoutId = setTimeout(() => {
            errors[0]?.scrollToElement();
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [errors, isSubmitted]);

    const size = useWindowSize().width >= 1760 ? 'xl' : 'lg';

    return (
        <div className="bg pb-[80px]">
            <PageHeader
                title={headerTitle}
                onCloseClickPath="/order-management/orders"
                additionalContent={headerAdditionalContent}
            />

            <main className="py-18 flex h-full w-full flex-col items-center gap-[40px] overflow-y-auto [counter-reset:section]">
                <MessageBox title={t('fulfillment.messageBox.title')} className="w-[720px]">
                    <p>{t('fulfillment.messageBox.text')}</p>
                </MessageBox>

                <FulfillmentSection title={t('fulfillment.clientInformation.title')}>
                    <ClientInformationFormFulfillment />
                </FulfillmentSection>

                <FulfillmentSection title={t('fulfillment.products.title')} size={size}>
                    <ErrorBoundary>
                        <ProductSelectionSection />
                    </ErrorBoundary>
                </FulfillmentSection>

                <FulfillmentSection title={t('fulfillment.documents.title')}>
                    <DocumentUploadSection />
                </FulfillmentSection>

                <FulfillmentSection title={t('fulfillment.summary.title')}>
                    <ErrorBoundary>
                        <Suspense fallback={<LoadingSpinner block />}>
                            <Summary />
                        </Suspense>
                    </ErrorBoundary>
                </FulfillmentSection>
            </main>

            <footer className="bg border-t-divider shadow-high fixed bottom-0 left-0 right-0 z-20 flex h-[80px] items-center justify-center">
                {footer}
            </footer>
        </div>
    );
};
